import { zodResolver } from '@hookform/resolvers/zod';
import type { CreateCustomerResponse } from '@weft/api-definition';
import { buildEmptyRevenueBreakdown } from '@weft/services';
import { NewCustomerSchema, OVERGAE_SETTINGS, type NewCustomer } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import { VyPageLayout } from 'apps/app/src/components/new-design/layout/VyPageLayout';
import { useCreateCustomer } from 'apps/app/src/hooks/requests/use-create-customer';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { CreateCustomerAddressBlock } from './create-customer-page-components/CreateCustomerAddressBlock';
import { CreateCustomerContactsBlock } from './create-customer-page-components/CreateCustomerContactsBlock';
import { CreateCustomerNameBlock } from './create-customer-page-components/CreateCustomerNameBlock';
import { CreateCustomerSettings } from './create-customer-page-components/CreateCustomerSettings';
import { DEFAULT_ADDRESS_COUNTRY,
  DEFAULT_DUE_DAYS,
  DEFAULT_SETTINGS_CURRENCY } from './create-customer-page-consts/create-customer-form-defaults';

export const CreateCustomerPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useVyNavigate();
  const revenueBreakdown = buildEmptyRevenueBreakdown();
  const methods = useForm<NewCustomer>({
    resolver: zodResolver(NewCustomerSchema),
    defaultValues: {
      revenueBreakdown,
      settings: {
        currency: DEFAULT_SETTINGS_CURRENCY,
        paymentDueDays: DEFAULT_DUE_DAYS,
        overage: OVERGAE_SETTINGS.ChargeTier,
      },
      address: {
        country: DEFAULT_ADDRESS_COUNTRY,
      },
    },
  });

  const createCustomerMutation = useCreateCustomer();

  const onCustomerCreated = async ({ customer }: CreateCustomerResponse) => {
    await queryClient.invalidateQueries('customersOverviews');
    navigate(location.pathname + `/../${customer.id}/settings`);
  };

  const onSubmit = methods.handleSubmit(async (data) => {
    const validatedNewCustomerInput = getValidNewCustomerInput(data);
    if (isNotNullOrUndefined(validatedNewCustomerInput)) {      
      await runMutation(
        createCustomerMutation,
        validatedNewCustomerInput, {
          pending: 'Saving...',
          success: 'Saved',
          error: 'Failed to saved customer',
        }, { onSuccess: onCustomerCreated });
    }
  });
  
  return (
    <>
      <Helmet>
        <title>Customers</title>
      </Helmet>
      <VyPageLayout
        title="Add Customer"
        sx={{
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <div className='flex flex-1'>
              <div className='px-12.5 flex flex-1 flex-col gap-12 bg-[#F6F9FA] py-11'>
                <CreateCustomerNameBlock/>
                <CreateCustomerAddressBlock/>
                <CreateCustomerContactsBlock/>
              </div>
              <CreateCustomerSettings/>    
            </div>
          </form>
        </FormProvider>
      </VyPageLayout>
    </>
  );
};

function getValidNewCustomerInput(newCustomerData: NewCustomer | null) {
  const newCustomerInputParseResult = NewCustomerSchema.safeParse(newCustomerData);

  return newCustomerInputParseResult.success ? newCustomerInputParseResult.data : undefined;
}